// Cloud designs list/load/delete. Legacy: openMyDesigns/loadCloudDesign/
// deleteCloudDesign (snapshot L9049-9144).
import { supabase } from './../auth/supabase'
import { getCanvas, state } from './../state'
import { resizeCanvas, fitToView } from './../canvas'
import { saveState } from './../history'
import { updateLayers } from './../panels/layers'
import { updateStatus } from './../statusbar'
import { migrateDesignJSON } from './../migration'
import { normalizeSizeAndSheet } from './../constants'
import { el, openModal, closeModal, toast } from './../ui/dom'

export interface DesignSummary {
  id: string
  name: string
  thumbnail: string | null
  updated_at: string
}

/** List the signed-in user's designs, newest first (legacy openMyDesigns query). */
export async function listDesigns(): Promise<DesignSummary[]> {
  if (!state.currentUser) return []
  const { data, error } = await supabase
    .from('designs')
    .select('id, name, thumbnail, updated_at')
    .eq('user_id', state.currentUser.id)
    .order('updated_at', { ascending: false })
  if (error) return []
  return (data ?? []) as DesignSummary[]
}

/** Load a design onto the canvas (legacy loadCloudDesign). */
export async function loadCloudDesign(id: string): Promise<boolean> {
  const { data, error } = await supabase.from('designs').select('*').eq('id', id).single()
  if (error || !data) return false

  // label_size may be the CLASSIC shape ({id,w,h,...}) on designs saved by the old
  // editor — assigning it raw left the canvas without widthMM/heightMM ("size not
  // supported", saves failing). Normalize via the catalogue (+ legacy id aliases).
  const hit = normalizeSizeAndSheet(data.label_size)
  if (hit) {
    state.currentSheet = hit.sheet // sheet rows ({id:'a4-21'} or classic 'sheet-a4-21') get their N-up layout back
    state.currentSize = hit.size
    state.orientation = data.orientation === 'portrait' ? 'portrait' : 'landscape'
    resizeCanvas()
  }
  state.batchHeaders = data.batch_headers ?? []
  state.batchData = data.batch_data ?? []

  const canvas = getCanvas()
  // Legacy designs are version "5.3.0" — normalize before loading (map §4.4 / Phase 6).
  await canvas.loadFromJSON(migrateDesignJSON(data.canvas_json))
  canvas.requestRenderAll()
  updateLayers()
  fitToView()
  saveState()

  state.currentDesignId = id
  state.currentDesignName = data.name || ''
  updateStatus()
  return true
}

/** Delete a design; clears the active id if it was the open one (legacy deleteCloudDesign). */
export async function deleteCloudDesign(id: string): Promise<boolean> {
  const { error } = await supabase.from('designs').delete().eq('id', id)
  if (error) return false
  if (state.currentDesignId === id) {
    state.currentDesignId = null
    state.currentDesignName = 'Untitled'
  }
  return true
}

/**
 * "My Designs" modal (account menu). Restyle of legacy openMyDesigns markup
 * (snapshot L8565-8579): a `.designs-grid` of `.design-card`s, each with a
 * thumbnail, name, date and a hover-revealed delete button. Clicking a card
 * loads the design; clicking delete removes it and re-renders.
 */
export function openDesignsModal(): void {
  // My Designs tab header (matches live, no team tab in v2 yet).
  const tabs = el('div', 'flex gap-0 mb-4 border-b-2 border-slate-700')
  const tabMy = el('button', 'px-4 py-2 text-sm font-semibold border-0 bg-transparent cursor-pointer -mb-0.5')
  tabMy.textContent = 'My Designs'
  tabMy.style.borderBottom = '2px solid #2563eb'
  tabMy.style.color = '#2563eb'
  tabs.append(tabMy)

  const loading = el('div', 'text-center text-slate-500 py-6', 'Loading...')
  const empty = el('div', 'text-center text-slate-500 py-6 hidden')
  empty.append(
    el('p', 'text-sm mb-2', 'No saved designs yet'),
    el('p', 'text-xs', 'Click Save or press Ctrl+S to save your current design'),
  )
  const grid = el('div', 'designs-grid hidden')

  const body = el('div')
  body.append(tabs, loading, empty, grid)

  openModal({ title: 'My Designs', body, width: '560px', testid: 'designs-modal' })

  const fmtDate = (iso: string): string => {
    const d = new Date(iso)
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString()
  }

  const renderCards = (designs: DesignSummary[]) => {
    loading.style.display = 'none'
    grid.innerHTML = ''
    if (!designs.length) {
      empty.classList.remove('hidden')
      grid.classList.add('hidden')
      return
    }
    empty.classList.add('hidden')
    grid.classList.remove('hidden')

    for (const d of designs) {
      const card = el('div', 'design-card')
      card.title = d.name || 'Untitled'

      const thumb = el('div', 'thumb')
      if (d.thumbnail) {
        const img = el('img') as HTMLImageElement
        img.src = d.thumbnail
        img.alt = d.name || 'design'
        thumb.append(img)
      }

      const name = el('div', 'name', d.name || 'Untitled')
      const date = el('div', 'date', fmtDate(d.updated_at))

      const del = el('button', 'delete-btn', '✕')
      del.title = 'Delete'
      del.addEventListener('click', async (e) => {
        e.stopPropagation()
        if (!window.confirm(`Delete "${d.name || 'Untitled'}"?`)) return
        const ok = await deleteCloudDesign(d.id)
        if (ok) { card.remove(); toast('Design deleted'); if (!grid.children.length) renderCards([]) }
        else toast('Could not delete design')
      })

      card.addEventListener('click', async () => {
        const ok = await loadCloudDesign(d.id)
        if (ok) { closeModal(); toast('Design loaded') }
        else toast('Could not load design')
      })

      card.append(thumb, name, date, del)
      grid.append(card)
    }
  }

  void listDesigns().then(renderCards).catch(() => renderCards([]))
}
